import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../hooks/use-auth";
import { apiRequest } from "../lib/queryClient";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import Sidebar from "../components/layout/sidebar";
import MobileSidebar from "../components/layout/mobile-sidebar";
import TopNav from "../components/layout/top-nav";
import { Shield, Loader2, Save } from "lucide-react";
import React, { useCallback, useMemo, useState } from "react";

const ROLE_LABELS: Record<string, string> = {
  admin: "Admin",
  manager: "Manager",
  agent: "Agent",
  worker: "Worker",
  view_only: "View only",
  accountant: "Accountant",
};

type PermissionsMatrix = Record<string, Record<string, boolean>>;
type Group = { group: string; permissions: { key: string; label: string }[] };

type RoleSetupPageProps = { embedInSettings?: boolean };

export default function RoleSetupPage({ embedInSettings }: RoleSetupPageProps = {}) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [localPermissions, setLocalPermissions] = useState<PermissionsMatrix | null>(null);

  const userRole = (user as { role?: string })?.role;
  const isOwner = userRole === "owner" || userRole === "contractor" || (user != null && (user as { userId?: number | null }).userId == null);

  const { data, isLoading } = useQuery<{ permissions: PermissionsMatrix; groups: Group[] }>({
    queryKey: ["/api/protected/role-permissions"],
    queryFn: async () => {
      const res = await apiRequest("GET", "/api/protected/role-permissions");
      if (!res.ok) throw new Error("Failed to load");
      return res.json();
    },
    enabled: isOwner,
  });

  const permissions = useMemo(() => localPermissions ?? data?.permissions ?? {}, [data?.permissions, localPermissions]);
  const groups = data?.groups ?? [];

  const saveMutation = useMutation({
    mutationFn: async (payload: { permissions: PermissionsMatrix }) => {
      const res = await apiRequest("PUT", "/api/protected/role-permissions", payload);
      if (!res.ok) throw new Error("Failed to save");
      return res.json();
    },
    onSuccess: (result) => {
      setLocalPermissions(null);
      queryClient.setQueryData(["/api/protected/role-permissions"], result);
      toast({ title: "Saved", description: "Role permissions updated." });
    },
    onError: (err: Error) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const setPermission = useCallback((role: string, key: string, value: boolean) => {
    setLocalPermissions((prev) => {
      const base = prev ?? data?.permissions ?? {};
      const next: PermissionsMatrix = {};
      for (const r of Object.keys(ROLE_LABELS)) {
        next[r] = { ...(base[r] ?? {}) };
      }
      next[role][key] = value;
      return next;
    });
  }, [data?.permissions]);

  const handleSave = () => {
    saveMutation.mutate({ permissions: localPermissions ?? data?.permissions ?? {} });
  };

  if (!user) return null;
  if (!isOwner) {
    const noAccess = (
      <Card className="border-[var(--dash-border)] bg-[var(--dash-card)]">
        <CardHeader>
          <CardTitle className="text-[var(--dash-text)]">Role setup</CardTitle>
          <CardDescription className="text-[var(--dash-text-muted)]">
            Only the account owner can configure what each role can see and do.
          </CardDescription>
        </CardHeader>
      </Card>
    );
    if (embedInSettings) return <div className="space-y-6">{noAccess}</div>;
    return (
      <div className="remodra-layout">
        <div className="hidden md:block"><Sidebar /></div>
        <MobileSidebar />
        <div className="remodra-main-mobile md:ml-64">
          <TopNav />
          <div className="remodra-content">
            <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24">
              <div className="w-full space-y-6">{noAccess}</div>
            </main>
          </div>
        </div>
      </div>
    );
  }

  const roleSetupContent = (
    <div className="space-y-6">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-semibold flex items-center gap-2 text-[var(--dash-text)]">
                    <Shield className="h-6 w-6" />
                    Role setup
                  </h1>
                  <p className="text-sm text-[var(--dash-text-muted)] mt-0.5">
                    Control what each role can see and do. For example, turn on &quot;View only own estimates&quot; for Agents so they only see their own estimates.
                  </p>
                </div>
                <Button
                  onClick={handleSave}
                  disabled={saveMutation.isPending || (localPermissions == null && !saveMutation.isPending)}
                >
                  {saveMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  <Save className="h-4 w-4 mr-2" />
                  Save changes
                </Button>
              </div>

              <Card className="border-[var(--dash-border)] bg-[var(--dash-card)] overflow-hidden">
                <CardHeader>
                  <CardTitle className="text-[var(--dash-text)]">Permissions by role</CardTitle>
                  <CardDescription className="text-[var(--dash-text-muted)]">
                    Allow or deny each permission per role. Owner always has full access.
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-0">
                  {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-[var(--dash-text-muted)]" />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-[var(--dash-border)] bg-[var(--dash-bg)]">
                            <th className="text-left p-3 font-medium text-[var(--dash-text)] w-48">Permission</th>
                            {Object.keys(ROLE_LABELS).map((role) => (
                              <th key={role} className="text-center p-3 font-medium text-[var(--dash-text)] min-w-[100px]">
                                {ROLE_LABELS[role]}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {groups.map((g) => (
                            <React.Fragment key={g.group}>
                              <tr className="border-b border-[var(--dash-border)] bg-[var(--dash-bg)]/50">
                                <td colSpan={1 + Object.keys(ROLE_LABELS).length} className="p-2 pl-3 font-medium text-[var(--dash-text-muted)]">
                                  {g.group}
                                </td>
                              </tr>
                              {g.permissions.map((p) => (
                                <tr key={p.key} className="border-b border-[var(--dash-border)] hover:bg-[var(--dash-bg)]/30">
                                  <td className="p-3 text-[var(--dash-text)]">{p.label}</td>
                                  {Object.keys(ROLE_LABELS).map((role) => (
                                    <td key={role} className="p-2 text-center">
                                      <input
                                        type="checkbox"
                                        checked={!!permissions[role]?.[p.key]}
                                        onChange={(e) => setPermission(role, p.key, e.target.checked)}
                                        className="h-4 w-4 rounded border-[var(--dash-border)]"
                                      />
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
    </div>
  );

  if (embedInSettings) return roleSetupContent;
  return (
    <div className="remodra-layout">
      <div className="hidden md:block"><Sidebar /></div>
      <MobileSidebar />
      <div className="remodra-main-mobile md:ml-64">
        <TopNav />
        <div className="remodra-content">
          <main className="dashboard-2025 bg-[var(--dash-bg)] min-h-screen p-6 md:p-8 pt-24">
            <div className="w-full space-y-6">
              {roleSetupContent}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
